import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { quizApi } from "@/lib/quizApi";
import { saveSession } from "@/lib/session";
import { BrandHeader } from "@/components/BrandHeader";

export default function OtpVerify() {
  const { quizId = "" } = useParams();
  const navigate = useNavigate();
  const candidateId = sessionStorage.getItem("pendingCandidateId") ?? "";

  const quiz = useQuery({
    queryKey: ["publicQuiz", quizId],
    queryFn: () => quizApi.getPublic(quizId),
    enabled: !!quizId,
  });

  const [code, setCode] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [channel, setChannel] = useState<"email" | "sms">("email");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const verify = useMutation({
    mutationFn: () => {
      if (quiz.data?.otpMode === "Both") {
        return quizApi.verifyOtp(quizId, candidateId, { emailCode, smsCode });
      }
      return quizApi.verifyOtp(quizId, candidateId, { channel, code });
    },
    onSuccess: (r) => {
      saveSession({ quizId, candidateId, token: r.token, expiresAt: r.expiresAt });
      sessionStorage.removeItem("pendingCandidateId");
      navigate(quiz.data?.idVerificationRequired
        ? `/quiz/${quizId}/id-upload`
        : `/quiz/${quizId}/instructions`);
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? "Verification failed."),
  });

  const resend = useMutation({
    mutationFn: (c: "email" | "sms") => quizApi.resendOtp(quizId, candidateId, c),
    onSuccess: (d) => { setInfo(`New code sent via ${d.channel} to ${d.maskedRecipient}.`); setError(null); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? "Could not resend code."),
  });

  if (!candidateId) return <Centered>Your session has expired. Please register again.</Centered>;
  if (quiz.isLoading) return <Centered>Loading…</Centered>;
  const mode = quiz.data!.otpMode;

  return (
    <>
      <BrandHeader />
      <main className="mx-auto max-w-md p-6">
      <div className="card p-8">
        <h1 className="text-2xl font-bold text-brand-ink">Enter verification code</h1>
        <p className="mt-1 text-sm text-brand-ink/60">Check your inbox or SMS for a 6-digit code.</p>

        {mode === "Both" ? (
          <div className="mt-6 space-y-4">
            <CodeField label="Email code" value={emailCode} onChange={setEmailCode} />
            <CodeField label="SMS code" value={smsCode} onChange={setSmsCode} />
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {mode === "Either" && (
              <div className="flex gap-4 text-sm">
                <label className="inline-flex items-center gap-2">
                  <input type="radio" checked={channel === "email"} onChange={() => setChannel("email")} /> Email
                </label>
                <label className="inline-flex items-center gap-2">
                  <input type="radio" checked={channel === "sms"} onChange={() => setChannel("sms")} /> SMS
                </label>
              </div>
            )}
            <CodeField label="Verification code" value={code} onChange={setCode} />
          </div>
        )}

        {info && <div className="mt-3 text-sm text-brand-ink/70">{info}</div>}
        {error && <div className="mt-3 text-sm text-brand">{error}</div>}

        <div className="mt-6 flex flex-wrap gap-3">
          <button className="btn-primary" onClick={() => verify.mutate()} disabled={verify.isPending}>
            {verify.isPending ? "Verifying…" : "Verify"}
          </button>
          {mode !== "Both" && (
            <button className="btn-secondary" onClick={() => resend.mutate(channel)} disabled={resend.isPending}>
              Resend {channel}
            </button>
          )}
          {mode === "Both" && (
            <>
              <button className="btn-secondary" onClick={() => resend.mutate("email")} disabled={resend.isPending}>
                Resend email
              </button>
              <button className="btn-secondary" onClick={() => resend.mutate("sms")} disabled={resend.isPending}>
                Resend SMS
              </button>
            </>
          )}
        </div>
      </div>
      </main>
    </>
  );
}

function CodeField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <input
        className="field-input tracking-widest"
        inputMode="numeric"
        maxLength={6}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
        placeholder="••••••"
      />
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <main className="grid min-h-screen place-items-center p-6 text-brand-ink/60">{children}</main>;
}
